import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  Divider,
  InputBase,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { tokens } from "../../theme";
import { userAPI } from "../../services/userAPI";
import { cardHolderAPI } from "../../services/cardHolderAPI";
import { nfcCardAPI } from "../../services/nfcCardAPI";
import { productAPI } from "../../services/productAPI";
import { branchAPI } from "../../services/branchAPI";
import { salesPointAPI } from "../../services/salesPointAPI";
import { categoryAPI } from "../../services/categoryAPI";

export type HeaderSearchPage = {
  text: string;
  path: string;
  module: string;
};

type SearchHit = {
  id: string;
  group: string;
  title: string;
  subtitle?: string;
  path: string;
};

type HeaderSearchProps = {
  pages: HeaderSearchPage[];
  canRead: (module: string) => boolean;
  branchId?: string | null;
};

const MIN_QUERY = 2;
const PAGE_SIZE = 5;
const DEBOUNCE_MS = 320;

function safeSettled<T>(p: Promise<T>): Promise<T | null> {
  return p.then((v) => v).catch(() => null);
}

const HeaderSearch: React.FC<HeaderSearchProps> = ({
  pages,
  canRead,
  branchId,
}) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [entityHits, setEntityHits] = useState<SearchHit[]>([]);
  const reqId = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const pageHits = useMemo(() => {
    const q = debounced.toLowerCase();
    if (!q) return [];
    return pages
      .filter((p) => p.text.toLowerCase().includes(q) || p.path.toLowerCase().includes(q))
      .slice(0, 8)
      .map(
        (p): SearchHit => ({
          id: `page:${p.path}`,
          group: "Pages",
          title: p.text,
          subtitle: p.path,
          path: p.path,
        })
      );
  }, [debounced, pages]);

  const canUsers = canRead("users");
  const canHolders = canRead("cardholders");
  const canCards = canRead("nfc_cards");
  const canProducts = canRead("products");
  const canBranches = canRead("branches");
  const canSalesPoints = canRead("sales_points");
  const canCategories = canRead("categories");

  useEffect(() => {
    if (debounced.length < MIN_QUERY) {
      setEntityHits([]);
      setLoading(false);
      return;
    }

    const id = ++reqId.current;
    setLoading(true);

    (async () => {
      const hits: SearchHit[] = [];
      const q = debounced;
      const tasks: Promise<void>[] = [];

      if (canUsers) {
        tasks.push(
          (async () => {
            const res = await safeSettled(
              userAPI.getUsers({ search: q, pageNo: 1, pageSize: PAGE_SIZE })
            );
            if (!res) return;
            for (const u of res.users) {
              hits.push({
                id: `user:${u.id}`,
                group: "Users",
                title: u.name,
                subtitle: [u.email, u.role].filter(Boolean).join(" · "),
                path: `/users/${u.id}`,
              });
            }
          })()
        );
      }

      if (canHolders) {
        tasks.push(
          (async () => {
            const res = await safeSettled(
              cardHolderAPI.getCardHolders({
                search: q,
                pageNo: 1,
                pageSize: PAGE_SIZE,
              })
            );
            if (!res) return;
            for (const h of res.items) {
              hits.push({
                id: `holder:${h.id}`,
                group: "Card holders",
                title: h.holderName,
                subtitle: h.phone || undefined,
                path: `/cardholders/${h.id}`,
              });
            }
          })()
        );
      }

      if (canCards) {
        tasks.push(
          (async () => {
            const res = await safeSettled(
              nfcCardAPI.getCards({
                search: q,
                page: 1,
                limit: PAGE_SIZE,
                ...(branchId ? { branchId } : {}),
              })
            );
            if (!res) return;
            for (const c of res.cards) {
              hits.push({
                id: `card:${c.id}`,
                group: "NFC cards",
                title: c.displayNumber,
                subtitle: c.holder?.holderName
                  ? `${c.holder.holderName}${c.holder.phone ? ` · ${c.holder.phone}` : ""}`
                  : c.status,
                path: `/nfc-cards?search=${encodeURIComponent(c.displayNumber)}`,
              });
            }
          })()
        );
      }

      if (canProducts) {
        tasks.push(
          (async () => {
            const res = await safeSettled(
              productAPI.getProducts({
                search: q,
                pageNo: 1,
                pageSize: PAGE_SIZE,
              })
            );
            if (!res) return;
            for (const p of res.data) {
              hits.push({
                id: `product:${p.id}`,
                group: "Products",
                title: p.name,
                subtitle: [p.sku, p.category?.name].filter(Boolean).join(" · "),
                path: `/products?search=${encodeURIComponent(p.name)}`,
              });
            }
          })()
        );
      }

      if (canBranches) {
        tasks.push(
          (async () => {
            const res = await safeSettled(
              branchAPI.getBranches({
                search: q,
                pageNo: 1,
                pageSize: PAGE_SIZE,
              })
            );
            if (!res) return;
            for (const b of res.data) {
              hits.push({
                id: `branch:${b.id}`,
                group: "Branches",
                title: b.name,
                subtitle: b.address || undefined,
                path: `/branches/${b.id}`,
              });
            }
          })()
        );
      }

      if (canSalesPoints) {
        tasks.push(
          (async () => {
            const res = await safeSettled(
              salesPointAPI.getSalesPoints({
                search: q,
                pageNo: 1,
                pageSize: PAGE_SIZE,
              })
            );
            if (!res) return;
            for (const s of res.data) {
              hits.push({
                id: `sp:${s.id}`,
                group: "Sales points",
                title: s.name,
                subtitle: s.branch?.name || s.description || undefined,
                path: `/sales-points/${s.id}`,
              });
            }
          })()
        );
      }

      if (canCategories) {
        tasks.push(
          (async () => {
            const res = await safeSettled(
              categoryAPI.getCategories({
                search: q,
                pageNo: 1,
                pageSize: PAGE_SIZE,
              })
            );
            if (!res) return;
            for (const c of res.data) {
              hits.push({
                id: `cat:${c.id}`,
                group: "Categories",
                title: c.name,
                subtitle: c.description || undefined,
                path: `/categories?search=${encodeURIComponent(c.name)}`,
              });
            }
          })()
        );
      }

      await Promise.all(tasks);
      if (reqId.current !== id) return;
      setEntityHits(hits);
      setLoading(false);
    })();
  }, [
    debounced,
    branchId,
    canUsers,
    canHolders,
    canCards,
    canProducts,
    canBranches,
    canSalesPoints,
    canCategories,
  ]);

  const grouped = useMemo(() => {
    const all = [...pageHits, ...entityHits];
    const map = new Map<string, SearchHit[]>();
    for (const hit of all) {
      const list = map.get(hit.group) || [];
      list.push(hit);
      map.set(hit.group, list);
    }
    return Array.from(map.entries());
  }, [pageHits, entityHits]);

  const hasResults = grouped.length > 0;
  const showPanel =
    open && (query.length > 0 || debounced.length > 0);

  const go = useCallback(
    (path: string) => {
      setOpen(false);
      setQuery("");
      setDebounced("");
      setEntityHits([]);
      navigate(path);
    },
    [navigate]
  );

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={anchorRef} sx={{ position: "relative" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: { xs: 160, md: 240 },
            maxWidth: "42vw",
            height: 28,
            px: 1,
            borderRadius: tokens.radius.sm,
            bgcolor: "background.default",
            border: "1px solid",
            borderColor: open ? "primary.main" : "divider",
            transition: "border-color 0.15s",
          }}
        >
          <SearchIcon sx={{ color: "text.secondary", fontSize: 16, mr: 0.75, flexShrink: 0 }} />
          <InputBase
            inputRef={inputRef}
            value={query}
            placeholder="Search…"
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            sx={{ flex: 1, fontSize: "0.75rem", minWidth: 0 }}
            inputProps={{ "aria-label": "Global search" }}
          />
          {loading ? (
            <CircularProgress size={12} sx={{ mr: 0.5 }} />
          ) : (
            <Typography
              component="span"
              sx={{
                fontSize: "0.625rem",
                color: "text.secondary",
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: tokens.radius.sm,
                px: 0.5,
                py: 0.1,
                lineHeight: 1.3,
                flexShrink: 0,
              }}
            >
              ⌘K
            </Typography>
          )}
        </Box>

        <Popper
          open={showPanel}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1400 }}
          modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
        >
          <Paper
            elevation={0}
            sx={{
              width: Math.max(anchorRef.current?.offsetWidth || 240, 320),
              maxWidth: "min(420px, 92vw)",
              maxHeight: 380,
              overflow: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: tokens.radius.sm,
              boxShadow: `0 8px 24px ${alpha("#000", 0.12)}`,
            }}
          >
            {debounced.length > 0 && debounced.length < MIN_QUERY && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 1.5, py: 1.25 }}>
                Type at least {MIN_QUERY} characters…
              </Typography>
            )}

            {debounced.length >= MIN_QUERY && !loading && !hasResults && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 1.5, py: 1.25 }}>
                No results in modules you can access
              </Typography>
            )}

            {grouped.map(([group, items], gi) => (
              <Box key={group}>
                {gi > 0 && <Divider />}
                <Typography
                  variant="overline"
                  sx={{
                    display: "block",
                    px: 1.5,
                    pt: 1,
                    pb: 0.25,
                    fontSize: "0.625rem",
                    color: "text.secondary",
                    letterSpacing: "0.06em",
                  }}
                >
                  {group}
                </Typography>
                <List dense disablePadding>
                  {items.map((hit) => (
                    <ListItemButton
                      key={hit.id}
                      onClick={() => go(hit.path)}
                      sx={{ py: 0.5, px: 1.5, alignItems: "flex-start" }}
                    >
                      <ListItemText
                        primary={hit.title}
                        secondary={hit.subtitle}
                        primaryTypographyProps={{
                          fontSize: "0.8125rem",
                          fontWeight: 600,
                          noWrap: true,
                        }}
                        secondaryTypographyProps={{
                          fontSize: "0.6875rem",
                          noWrap: true,
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            ))}

            {debounced.length >= MIN_QUERY && hasResults && (
              <Stack
                direction="row"
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderTop: "1px solid",
                  borderColor: "divider",
                  bgcolor: alpha(tokens.colors.neutral[100], 0.6),
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Only modules you can read are searched
                </Typography>
              </Stack>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default HeaderSearch;
